import { DefaultEventEmitter } from "@baseline/runtime/events";
import classNames, { Argument as ClassNameArgument } from "classnames";
import React, { CSSProperties, FC, ReactNode, useCallback, useState } from "react";
import { Size } from "../dom";
import { useController, useListener } from "../react";
import { styles } from "./resizable-box.scss";


export function ResizeBox(props: ResizeBox.Props): ReactNode
{
    return (
        (props.transition) ?
            TransitionalResizeBox(props) :
            BasicResizeBox(props)
    );
};

namespace ResizeBox
{
    export interface Props
    {
        resize?: Resize;
        onResize?: (size: Size) => void;
        className?: ClassNameArgument;
        style?: CSSProperties;
        transition?: boolean;
        children?: ReactNode;
    }

    export type Resize = "width" | "height" | "both";
}

export const ResizableBox: FC<ResizeBox.Props> = ResizeBox;


function TransitionalResizeBox({ resize, onResize, className, style, children }: ResizeBox.Props): ReactNode
{
    const [size, setSize] = useState<Size>();

    const $onResize =
        useCallback(
            size =>
            {
                onResize?.(size);
                setSize(size);
            },
            [onResize]
        );

    return (
        <div className={classNames(styles.resizeBox, styles.transitional, className)} style={{ ...style, ...size }}>
            <BasicResizeBox resize={resize} onResize={$onResize}>
                {children}
            </BasicResizeBox>
        </div>
    );
}

function BasicResizeBox({ resize, onResize, className, style, children }: ResizeBox.Props): ReactNode
{
    const controller = useController(() => new BasicResizeBox.Controller(resize), [resize]);
    useListener(controller, "resize", onResize);

    return (
        <div ref={controller.useElement} className={classNames(className)} style={style}>
            {children}
        </div>
    );
}

namespace BasicResizeBox
{
    export class Controller extends DefaultEventEmitter<Event>
    {
        constructor(
            readonly resize: ResizeBox.Resize = "both"
        )
        {
            super();
            this.useElement = this.useElement.bind(this);
            this.refresh = this.refresh.bind(this);
        }

        useElement(element: HTMLDivElement): void
        {
            this.element = element;
            if (element)
            {
                this.resizeObserver = new ResizeObserver(this.refresh);
                this.resizeObserver.observe(element);
                this.refresh();
            }
            else
            {
                this.resizeObserver.disconnect();
                delete this.resizeObserver;
            }
        }

        element: HTMLDivElement;


        private refresh(): void
        {
            const width = (this.resize != "height") && this.element.offsetWidth;
            const height = (this.resize != "width") && this.element.offsetHeight;

            const size: Partial<Size> =
            {
                ...(typeof width == "number" && { width }),
                ...(typeof height == "number" && { height })
            };

            this.emit("resize", size);
        }

        private resizeObserver: ResizeObserver;
    }

    export type Event = "resize";
}
